import jsPDF from 'jspdf';
// The project also loads @types/jspdf v1. Describe the two installed v3 APIs
// absent from those legacy declarations without changing global dependency types.
type PdfDocument = jsPDF & {
  getImageProperties(image: string): {width: number; height: number};
  getNumberOfPages(): number;
};

// Presentation only. Callers retain permissions, clearance/QR services and uploads.
export interface LetterData {
  kind: 'approval' | 'clearance'; date: string;
  applicantName: string; applicantSurname: string; researchTitle: string;
  faculty: string; applicantCategory: string; clearanceNumber: string;
  verificationUrl: string; qrData: string; signatureDataUrl: string; signatoryName: string;
}
export interface LetterAssets { regular: string; semibold: string; bold: string; crest: string; }
const C = { sky: '#A4D8EE', gold: '#F8CF29', red: '#F21A29', blue: '#0086B8',
  ink: '#28363E', muted: '#61758A', border: '#D7E5ED', pale: '#F2FAFD' };
let assetsPromise: Promise<LetterAssets> | undefined;
const assetBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
async function asset(path: string, dataUrl = false): Promise<string> {
  const response = await window.fetch(assetBase + path);
  if (!response.ok) throw new Error('Unable to load letter asset: ' + path);
  const bytes = new Uint8Array(await response.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 8192) binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + 8192)));
  return (dataUrl ? 'data:image/png;base64,' : '') + btoa(binary);
}
export function loadLetterAssets(): Promise<LetterAssets> {
  if (!assetsPromise) assetsPromise = Promise.all([
    asset('/fonts/poppins/Poppins-Regular.ttf'), asset('/fonts/poppins/Poppins-SemiBold.ttf'),
    asset('/fonts/poppins/Poppins-Bold.ttf'), asset('/ojs_brand.png', true),
  ]).then(([regular, semibold, bold, crest]) => ({regular, semibold, bold, crest})).catch(error => {
    assetsPromise = undefined; throw error;
  });
  return assetsPromise;
}
type Weight = 'normal' | 'semibold' | 'bold';
type Run = {text: string; weight?: Weight};
const bold = (text: string): Run => ({text, weight: 'semibold'});
// Literal PDF text, never HTML. Preserve punctuation and strip nonprinting controls.
// eslint-disable-next-line no-control-regex
const clean = (text: string) => String(text).replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '').replace(/\s+/g, ' ').trim();
export async function generateLetterPdf(data: LetterData): Promise<Blob> {
  return renderLetterPdf(data, await loadLetterAssets()).output('blob');
}

/** Used by the application and real-PDF regression fixtures. */
export function renderLetterPdf(data: LetterData, assets: LetterAssets): PdfDocument {
  const pdf = new jsPDF({orientation: 'p', unit: 'pt', format: 'a4', putOnlyUsedFonts: true, compress: true}) as unknown as PdfDocument;
  ([['normal', assets.regular], ['semibold', assets.semibold], ['bold', assets.bold]] as const).forEach(([style, bytes]) => {
    const filename = 'Poppins-' + style + '.ttf';
    pdf.addFileToVFS(filename, bytes); pdf.addFont(filename, 'Poppins', style);
  });
  const W = pdf.internal.pageSize.getWidth(), H = pdf.internal.pageSize.getHeight();
  const left = 48, width = W - 96, bottom = 632, size = 9.5, leading = 16;
  let y = 0;
  function font(weight: Weight = 'normal', fontSize = size, color = C.ink) {
    pdf.setFont('Poppins', weight); pdf.setFontSize(fontSize); pdf.setTextColor(color);
  }
  function label(text: string, x: number, top: number, fontSize: number, weight: Weight = 'normal', color = C.ink) {
    font(weight, fontSize, color); pdf.text(clean(text), x, top);
  }
  function rule(x: number, top: number, length: number, color = C.border, thickness = 0.6) {
    pdf.setDrawColor(color); pdf.setLineWidth(thickness); pdf.line(x, top, x + length, top);
  }
  function imageFit(image: string, x: number, top: number, w: number, h: number) {
    const props = pdf.getImageProperties(image), scale = Math.min(w / props.width, h / props.height);
    const iw = props.width * scale, ih = props.height * scale;
    pdf.addImage(image, 'PNG', x + (w - iw) / 2, top + (h - ih) / 2, iw, ih);
  }
  function header() {
    pdf.setFillColor(C.sky); pdf.rect(0, 0, W * .69, 7.5, 'F');
    pdf.setFillColor(C.gold); pdf.rect(W * .69, 0, W * .285, 7.5, 'F');
    pdf.setFillColor(C.red); pdf.rect(W * .975, 0, W * .025, 7.5, 'F');
    imageFit(assets.crest, 40, 49, 88, 88);
    label('MIDLANDS STATE UNIVERSITY', 135, 60, 15.5, 'bold');
    label('RESEARCH AND INNOVATION DIVISION', 135, 81, 9.3, 'semibold', C.blue);
    ['P. Bag 9055, Gweru, Zimbabwe', 'Tel: (263) 54 260416/7, 260331', 'mehlanag@staff.msu.ac.zw']
      .forEach((line, i) => label(line, 135, 102 + i * 14, 7.7, 'normal', C.muted));
    rule(40, 149, W - 80); rule(40, 149, 105, C.gold, 3);
    font('semibold', 8);
    const refs: string[] = pdf.splitTextToSize('REFERENCE  ' + clean(data.clearanceNumber), W - 215);
    font('normal', 8); const dates: string[] = pdf.splitTextToSize(clean(data.date), 107);
    const panelHeight = 37 + Math.max(refs.length, dates.length) * 11;
    pdf.setFillColor(C.pale); pdf.roundedRect(40, 162, W - 80, panelHeight, 8, 8, 'F');
    pdf.setFillColor(C.blue); pdf.roundedRect(40, 162, 4, panelHeight, 2, 2, 'F');
    label(data.kind === 'approval' ? 'APPROVAL FOR ETHICAL CLEARANCE' : 'RESEARCH ETHICAL CLEARANCE', 56, 182, 12, 'bold');
    refs.forEach((line, i) => label(line, 56, 199 + i * 11, 8, 'semibold', C.blue));
    dates.forEach((line, i) => { font('normal', 8, C.muted); pdf.text(line, W - 56, 199 + i * 11, {align: 'right'}); });
    y = 162 + panelHeight + 21;
  }
  function nextPage() { pdf.addPage(); header(); }
  function ensure(height: number) { if (y + height > bottom) nextPage(); }
  function lines(runs: Run[], maxWidth = width, fontSize = size): Run[][] {
    const result: Run[][] = []; let line: Run[] = [], used = 0;
    font('normal', fontSize); const space = pdf.getTextWidth(' ');
    for (const run of runs) for (const word of clean(run.text).split(' ').filter(Boolean)) {
      font(run.weight, fontSize);
      const pieces: string[] = pdf.getTextWidth(word) > maxWidth ? pdf.splitTextToSize(word, maxWidth) : [word];
      for (const piece of pieces) {
        const measured = pdf.getTextWidth(piece);
        if (line.length && used + space + measured > maxWidth) { result.push(line); line = []; used = 0; }
        if (line.length) used += space;
        line.push({text: piece, weight: run.weight}); used += measured;
      }
    }
    if (line.length) result.push(line);
    return result;
  }
  function paragraph(runs: Run[], gap = 12, justify = false) {
    const wrapped = lines(runs);
    wrapped.forEach((line, index) => {
      ensure(index === 0 && wrapped.length > 1 ? leading * 2 : leading);
      font(); const space = pdf.getTextWidth(' ');
      const wordsWidth = line.reduce((sum, word) => { font(word.weight); return sum + pdf.getTextWidth(word.text); }, 0);
      const spacing = justify && index < wrapped.length - 1 && line.length > 1 ? (width - wordsWidth) / (line.length - 1) : space;
      let x = left;
      for (const word of line) { font(word.weight); pdf.text(word.text, x, y); x += pdf.getTextWidth(word.text) + spacing; }
      y += leading;
    });
    y += gap;
  }
  function signature() {
    const boxWidth = 338, innerWidth = boxWidth - 32;
    font('semibold', 10); const names: string[] = pdf.splitTextToSize(clean(data.signatoryName), innerWidth);
    const designation = data.kind === 'approval' ? 'Executive Director- MSU Research and Innovation Division' : 'Chairperson- MSU Research Ethics Committee';
    font('normal', 7.5); const roles: string[] = pdf.splitTextToSize(designation, innerWidth);
    const height = 61 + names.length * 13 + roles.length * 11;
    ensure(height + 16);
    paragraph([{text: data.kind === 'approval' ? 'Yours sincerely' : 'Yours Sincerely'}], 0);
    const top = y;
    pdf.setFillColor('#F8FBFD'); pdf.roundedRect(left, top, boxWidth, height, 7, 7, 'F');
    pdf.setFillColor(C.gold); pdf.roundedRect(left, top, 3, height, 1.5, 1.5, 'F');
    label('AUTHORISED SIGNATORY', left + 16, top + 18, 7.3, 'semibold', C.blue);
    if (data.signatureDataUrl) imageFit(data.signatureDataUrl, left + 16, top + 21, 196, 25);
    rule(left + 16, top + 48, 196, C.muted, .8);
    names.forEach((line, i) => label(line, left + 16, top + 64 + i * 13, 10, 'semibold'));
    roles.forEach((line, i) => label(line, left + 16, top + 65 + names.length * 13 + i * 11, 7.5, 'normal', C.muted));
    y = top + height;
  }
  function footer(last: boolean, page: number, count: number) {
    rule(40, 650, W - 80);
    label('OFFICIAL DOCUMENT VERIFICATION', 42, 665, 7.3, 'semibold', C.blue);
    font('semibold', 8); const refs: string[] = pdf.splitTextToSize('Ref: ' + clean(data.clearanceNumber), 350);
    refs.forEach((line, i) => label(line, 42, 683 + i * 11, 8, 'semibold'));
    if (/\[TEST ONLY\]/i.test(data.researchTitle)) label('TEST ONLY - NOT VALID FOR REAL RESEARCH.', 42, 694 + refs.length * 11, 7.5, 'normal', C.red);
    if (last) {
      const x = W - 177, top = 666, cardWidth = 132, cardHeight = 135;
      pdf.setFillColor('#F1F4F6'); pdf.roundedRect(x + 3, top + 4, cardWidth, cardHeight, 12, 12, 'F');
      pdf.setFillColor('#FFFFFF'); pdf.setDrawColor(C.border); pdf.setLineWidth(.7);
      pdf.roundedRect(x, top, cardWidth, cardHeight, 12, 12, 'FD');
      pdf.setFillColor(C.gold); pdf.roundedRect(x + 13, top + 14, 4, 10, 2, 2, 'F');
      label('DOCUMENT QR', x + 23, top + 21, 7.5, 'semibold');
      // Preserve the backend PNG and its quiet zone, without recolouring/resampling.
      imageFit(data.qrData, x + 19, top + 28, 94, 94);
      font('semibold', 7, C.blue); pdf.text('SCAN TO VERIFY', x + cardWidth / 2, top + 123, {align: 'center'});
      pdf.link(x + 19, top + 28, 94, 94, {url: data.verificationUrl});
    }
    rule(40, H - 22, W - 80);
    label('MIDLANDS STATE UNIVERSITY', 40, H - 12, 6.7, 'normal', C.muted);
    font('normal', 6.7, C.muted); pdf.text('RESEARCH ETHICS  •  GWERU, ZIMBABWE', W - 40, H - 12, {align: 'right'});
    if (count > 1) { font('normal', 7, C.muted); pdf.text(page + ' / ' + count, W / 2, H - 12, {align: 'center'}); }
  }
  header();
  const name = data.applicantName + ' ' + data.applicantSurname;
  if (data.kind === 'approval') {
    paragraph([bold(data.researchTitle)], 0); paragraph([bold(name)], 8);
    paragraph([{text: 'P Bag 9055'}], 0); paragraph([{text: 'Senga'}], 0); paragraph([{text: 'GWERU'}], 8);
    paragraph([{text: 'Dear ' + name}], 16);
    paragraph([bold('RE: APPROVAL FOR ETHICAL CLEARANCE NUMBER ' + data.clearanceNumber + '.')], 0);
    paragraph([bold('‘' + data.researchTitle + '’')], 12);
    paragraph([{text: 'I am pleased to inform you that your application for ethical clearance of the above-mentioned study was approved based on the recommendation of the Faculty Research Ethics Committee.'}], 12, true);
    paragraph([{text: 'The ethical clearance is valid for the duration of the study.'}], 0, true);
  } else {
    paragraph([bold('TO WHOM IT MAY CONCERN')], 16);
    paragraph([bold('RE: ' + name.toUpperCase() + "'s RESEARCH ETHICAL CLEARANCE, " + data.clearanceNumber)], 12);
    paragraph([{text: 'This letter serves to confirm that'}, bold(name), {text: 'is a Bonafide ' + data.applicantCategory + ' of Midlands State University in the'}, bold(data.faculty + ' Faculty.')], 12, true);
    paragraph([{text: 'The research entitled,'}, bold("'" + data.researchTitle + "'"), {text: 'went through the research ethics clearance process and was found to meet the requirements as prescribed in the research ethics policy.'}], 12, true);
    paragraph([{text: 'Kindly assist with information relating to this study.'}], 0, true);
  }
  signature();
  const count = pdf.getNumberOfPages();
  for (let page = 1; page <= count; page++) { pdf.setPage(page); footer(page === count, page, count); }
  return pdf;
}
